import { Box } from "@mui/material";
import { batch, computed, ReadonlySignal, signal, Signal, useSignal, useSignalEffect } from "@preact/signals-react";
import { isNil, isObject } from "lodash-es";
import { useEffect } from "react";
import { Component, LayoutItem } from "../../resolvers-types";
import { GetComponent } from "../library/components";
import {
  getScope,
  getState,
  isSimpleValue,
  meta,
  setupLocalState,
  shouldReturnSignal,
  updateItemContext,
} from "../library/dataService";
import { getAst, getExprValue } from "../library/interpreter";
import { log } from "../library/logger";
import { getTypename } from "../library/metadataUtils";
import { INGReferenceProps, RuntimeContext } from "../library/NGFieldExtensions";
import { getClassName, getsxObject, isNullOrEmpty, isVisible } from "../library/utils";
import NGLayoutItem from "./NGLayoutItem";

const tag = "NGReference";

async function retrieveReferenceMetadata(id, refId: string, refType: string, _context: RuntimeContext) {
  switch (refType) {
    case "Component": {
      const c = await GetComponent(refId, _context);

      if (isNil(c)) log.error(tag, `Component '${refId}' not found for Reference '${id}'`);

      if (c != null && isNullOrEmpty(getTypename(c))) c.__typename = "Component"; // This should not be necessary once issue with project.save is resolved

      return c;
    }
    default:
      throw new Error(`Reference type ${refType} not implemented`);
  }
}

export default function NGReference({ config, context }: INGReferenceProps) {
  const currentContext = updateItemContext(context, config);

  const local = setupLocalState(
    config,
    {
      Inputs: useSignal(config.Inputs ?? {}),
      Visible: useSignal(config.Visible ?? true),
      Classes: useSignal(config.Classes ?? []),
      Style: useSignal(config.Style ?? {}),
      ReferenceId: useSignal(config.ReferenceId),
      ReferenceSource: useSignal(config.ReferenceSource ?? null),
      ReferenceType: useSignal(config.ReferenceType),
      HydrateStateFrom: useSignal(config.HydrateStateFrom ?? null),
    },
    currentContext
  );

  const componentFromReference = useSignal<Component | null>(null);
  const contextFromReference = useSignal<RuntimeContext | null>(null);

  // using useEffect in order to re-run when reference configuration changes
  useSignalEffect(() => {
    const inputs = local.Inputs.value;
    const refId = local.ReferenceId.value;
    const refType = local.ReferenceType.value;
    const fetchComponents = async () => {
      log.info(tag, "useSignalEffect", config);
      // await delay(1000);

      retrieveReferenceMetadata(config.Id, refId, refType, currentContext).then((c) => {
        if (c == null) return;
        c = { ...c, Id: c.Id + "-" + config.Id, ComponentId: c.Id };

        // If this is an embedded reference, auto-bind all component inputs to Parent.X
        const { global } = getState(currentContext);
        const isEmbedded = global.IsEmbeded.value;
        let finalInputs = inputs;

        if (isEmbedded && c.Inputs) {
          const autoBindings: Record<string, string> = {};

          // Create Parent.X bindings for each component input
          Object.keys(c.Inputs).forEach((inputKey) => {
            autoBindings[inputKey] = `Parent.${inputKey}`;
          });

          // Merge auto-bindings with existing reference inputs
          finalInputs = { ...autoBindings, ...inputs };

          log.info(tag, `Auto-binding component inputs for embedded reference ${config.Id}:`, autoBindings);
        }

        //const inputs = (config as Reference).Inputs;
        const childContext = updateItemContext(currentContext, { ...c });
        const { state, form, parentState } = getState(childContext);
        const scope = getScope(childContext, config, state, form, {}, parentState);
        if (!isNil(finalInputs)) {
          Object.entries(finalInputs).forEach(([k, v]) => {
            if (isNullOrEmpty(v as string)) return;

            if (typeof v !== "string") {
              state[k] = signal(v);
              // console.error("Invalid input value: '" + v + "' has to be a string but it is a " + typeof v + "instead");
              return;
            }

            const ast = getAst(v);
            const returnSignal = shouldReturnSignal(ast);

            let src: Signal | ReadonlySignal | null = null;

            if (returnSignal) {
              src = getExprValue(v as string, scope, null, returnSignal);
            } else if (isSimpleValue(ast)) {
              src = signal(getExprValue(v as string, scope, null, returnSignal));
            } else {
              src = computed(() => getExprValue(v as string, scope, null));
            }
            //const src = getExprValue(v as string, scope, null, returnSignal);
            state[k] = src;
          });
        }

        if (!isNullOrEmpty(local.HydrateStateFrom.value) && isObject(local.HydrateStateFrom.value)) {
          Object.entries(local.HydrateStateFrom.value).forEach(([key, value]) => {
            state[key] = signal(value);
          });
        }

        batch(() => {
          componentFromReference.value = { ...c };
          contextFromReference.value = { ...childContext };
        });
        // Trigger meta version change in next rendering cycle.
        // This ensures that any listeners that are doing dom lookups from meta will get correctly sized elements
        // E.g. designer overlay
        setTimeout(() => {
          meta.version.value = meta.version.peek() + 1;
        }, 0);

        log.info(tag, "retrieveReferenceMetadata", c);
      });
    };
    fetchComponents();

    // re-run when reference configuration changes
  });

  return (
    <>
      {isVisible(local.Visible.value, config, currentContext) && (
        <>
          <Box
            data-testid={config.Id}
            data-type={config.__typename}
            className={getClassName(local.Classes)}
            sx={getsxObject(local.Style.value)}
          >
            {componentFromReference.value && contextFromReference.value && (
              <>
                {/* removed renderModalPopups from here because NGComponent already does this for us */}
                {/* popups were getting rendered twice in DOM observable in devtools */}
                {/* {renderModalPopups(componentFromReference.value, contextFromReference.value)} */}
                <NGLayoutItem
                  context={contextFromReference.value}
                  config={componentFromReference.value as LayoutItem}
                />
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
}
